import { useQuery } from '@tanstack/react-query'
import * as R from 'remeda'

import { graphql } from '~/gql'
import { useGraphQLRequest } from '~/hooks/useGraphQLRequest'
import {
  applyIfExist,
  applyOr,
  filterExists,
  SurveyOp,
  updateProp,
} from '~/utils'

import { useBrandAccountQueryKey } from './useBrandAccountQueryKey'

const document = graphql(`
  query UseBrandAccountQuery_Main($brandAccountId: ID!) {
    brandAccount(brandAccountId: $brandAccountId) {
      id
      surveys(questionDisplayFilter: DETAIL_COUNT) {
        id
        title
        numberOfTotalAnswers
        metadata {
          tamNumber
          surveyStartDate {
            year
            month
            day
          }
        }
        brandGroups {
          id
          name
          brands {
            id
            name
            number
          }
        }
        questions {
          question_id
          question_text
        }
        customerAttributeGroups {
          id
          name
        }
      }
    }
  }
`)

export const useBrandAccountQuery = () => {
  const queryKey = useBrandAccountQueryKey()
  const graphQLRequest = useGraphQLRequest()
  const [_, variables] = queryKey

  return useQuery({
    queryFn: async () => {
      const result = await graphQLRequest({ document, variables })

      return R.pipe(
        result.brandAccount,
        applyIfExist(
          updateProp(
            'surveys',
            applyOr(
              R.piped(
                filterExists,
                R.sort(SurveyOp.compareByStartDate),
                R.map(
                  R.piped(
                    updateProp('metadata', applyIfExist(R.identity())),
                    updateProp(
                      'brandGroups',
                      R.piped(
                        filterExists,
                        R.map(updateProp('brands', filterExists)),
                      ),
                    ),
                    updateProp('questions', filterExists),
                    updateProp('customerAttributeGroups', filterExists),
                  ),
                ),
              ),
              [],
            ),
          ),
        ),
      )
    },
    queryKey,
  })
}

export type BrandAccount = NonNullable<
  QueryResultOf<typeof useBrandAccountQuery>
>
export type Survey = BrandAccount['surveys'][number]
export type BrandGroup = Survey['brandGroups'][number]
export type Brand = BrandGroup['brands'][number]
